/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { animate, AnimationResponsivePopupEnterName, baseStyles, event, getElementUpdates, i18n, I18nService, isScrollable, property, querySlot, reverseAnimation, setAttributes, setPopupPosition, state, ariaPopup, } from '@cds/core/internal';
import { html } from 'lit';
import { query } from 'lit/decorators/query.js';
import { CdsInternalStaticOverlay } from '@cds/core/internal-components/overlay';
import sharedStyles from '../overlay/shared.element.scss';
import styles from './popup.element.scss';
/**
 * ```typescript
 * import '@cds/core/internal-components/popup/register.js';
 * ```
 *
 * ```html
 * <cds-internal-popup>
 *  <section cds-layout="vertical align:horizontal-stretch">
 *    <div cds-layout="vertical pad:md gap:md">
 *      <h2 cds-text="display">A Title</h2>
 *      <div>
 *        <p cds-text="body">
 *          Content inside a generic popup.
 *        </p>
 *      </div>
 *    </div>
 *  </section>
 * </cds-internal-popup>
 * ```
 *
 * @beta
 * @element cds-internal-popup
 * @slot - Content slot for the content inside the popup's panel
 * @event closeChange - Notify user when close event has occurred
 * @cssprop --active-corner-border-radius
 * @cssprop --background
 * @cssprop --backdrop-background
 * @cssprop --layered-backdrop-background
 * @cssprop --border-color
 * @cssprop --border-radius
 * @cssprop --border-width
 * @cssprop --box-shadow
 * @cssprop --close-button-offset
 * @cssprop --color
 * @cssprop --min-height
 * @cssprop --min-width
 * @cssprop --max-height
 * @cssprop --max-width
 * @cssprop --mobile-max-height
 * @cssprop --overflow - sets overflow x and y values respectively
 * @cssprop --height
 * @cssprop --width
 * @cssprop --animation-duration
 * @cssprop --animation-easing
 *
 * KNOWN ISSUE: Safari jumps through the exit animation but only when the ESC key is pressed.
 *
 */
let CdsInternalPopup = class CdsInternalPopup extends CdsInternalStaticOverlay {
    constructor() {
        // --- mixins/composables ---
        super(...arguments);
        this.i18n = I18nService.keys.popup;
        this.cdsMotion = 'on';
        /**
         * force responsive by setting orientation to 'none'.
         * do not manipulate the responsive property because the
         * positioning logic may override your changes.
         */
        /** @private */
        this.responsive = false;
        this.anchorAlign = 'start';
        // --- pointer ---
        this.pointerAlign = 'start';
        // TODO: TESTME
        /**
         * Forces a pointer to show with popup from known pointer types
         * @type {'angle' | 'default'}
         */
        this.defaultPointerType = null;
        // --- observers and callbacks ---
        this.observers = [];
    }
    // this getter is stealth private so we can test it in isolation
    /** @private */
    get anchorElement() {
        const anchor = this.anchor;
        // if string we have an id
        if (typeof anchor === 'string') {
            // check shadowRoot first in the event that we have a wrapper web component
            const shadowAnchor = this.getRootNode().querySelector(`#${anchor}`);
            if (shadowAnchor === null) {
                // check document/light-dom next; lookup below returns an element or null
                return this.getRootNode({ composed: true }).querySelector(`#${this.anchor}`);
            }
            else {
                return shadowAnchor;
            }
        }
        // anchor is not an id/string; so return whatever it is
        return anchor;
    }
    /** @private */
    get anchorRect() {
        return this.anchorElement?.getBoundingClientRect() || new DOMRect();
    }
    setUpPositioningObserver() {
        this.observers.push(getElementUpdates(this, 'hidden', () => {
            if (this.hidden === false) {
                setPopupPosition(this);
            }
        }));
    }
    connectedCallback() {
        if (!this.pointer && this.defaultPointerType) {
            const defaultPointer = document.createElement('cds-internal-pointer');
            defaultPointer.type = this.defaultPointerType;
            this.appendChild(defaultPointer);
        }
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.observers.forEach(o => o?.disconnect());
    }
    // --- lifecycle ---
    firstUpdated(props) {
        super.firstUpdated(props);
        this.setUpPositioningObserver();
    }
    updated(props) {
        if (this.pointer && !this.pointer.hasAttribute('part')) {
            setAttributes(this.pointer, ['part', 'pointer']);
        }
        if (props.has('hidden')) {
            setAttributes(this.contentWrapper, ['tabindex', isScrollable(this.contentWrapper) ? '0' : false]);
        }
        super.updated(props);
    }
    // --- render and styles ---
    render() {
        return html `
      ${this.backdropTemplate}
      <div class="private-host" tabindex="-1">
        <div cds-layout="display:screen-reader-only">${this.i18n.contentStart}</div>
        <div class="popup-wrapper">
          <div class="popup-content">
            ${this.closable || this.responsive ? this.closeButtonTemplate : ''}
            <slot></slot>
          </div>
          <div class="pointer-wrapper">
            <div class="popup-pointer"><slot name="pointer"></slot></div>
          </div>
        </div>
        <div cds-layout="display:screen-reader-only">${this.i18n.contentEnd}</div>
      </div>
    `;
    }
    static get styles() {
        return [baseStyles, styles, sharedStyles];
    }
};
__decorate([
    i18n()
], CdsInternalPopup.prototype, "i18n", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "cdsMotion", void 0);
__decorate([
    state({ type: Boolean, reflect: true, attribute: 'responsive' })
], CdsInternalPopup.prototype, "responsive", void 0);
__decorate([
    event()
], CdsInternalPopup.prototype, "cdsMotionChange", void 0);
__decorate([
    property({ type: Object })
], CdsInternalPopup.prototype, "trigger", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "anchor", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "anchorAlign", void 0);
__decorate([
    property({ type: Number })
], CdsInternalPopup.prototype, "mainAxisOffset", void 0);
__decorate([
    property({ type: Number })
], CdsInternalPopup.prototype, "crossAxisOffset", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "orientation", void 0);
__decorate([
    property({ type: Boolean })
], CdsInternalPopup.prototype, "closable", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "pointerAlign", void 0);
__decorate([
    property({ type: String })
], CdsInternalPopup.prototype, "defaultPointerType", void 0);
__decorate([
    querySlot('cds-internal-pointer', { assign: 'pointer' })
], CdsInternalPopup.prototype, "pointer", void 0);
__decorate([
    query('.popup-pointer')
], CdsInternalPopup.prototype, "pointerWrapper", void 0);
__decorate([
    query('.private-host')
], CdsInternalPopup.prototype, "hostWrapper", void 0);
__decorate([
    query('.popup-wrapper')
], CdsInternalPopup.prototype, "popupWrapper", void 0);
__decorate([
    query('.popup-content')
], CdsInternalPopup.prototype, "contentWrapper", void 0);
CdsInternalPopup = __decorate([
    ariaPopup(),
    animate({
        hidden: {
            true: reverseAnimation(AnimationResponsivePopupEnterName),
            false: AnimationResponsivePopupEnterName,
        },
    })
], CdsInternalPopup);
export { CdsInternalPopup };
